// Global Instructions Rule Applied!
//
// Distribution screen (docs/WATERFALL_CARRY_DESIGN.md, phase 3: the
// Distribution transaction on the capital-call lifecycle).
//
// Draft-first workflow on the capital call form's pattern: header (fund /
// name / kind / date / gross amount) + the sources (the realizations the
// proceeds come from) + the allocation: the Tiers strip and one row per
// investor with the tiers left to right (phase 6 PR 2b). A user overrides
// what an investor is paid - its cash, its carry - never the waterfall; the
// server spreads an override over the tier rows. Every save round-trips
// through POST /distributions (wf__save_distribution) and re-hydrates from
// the returned detail. Compute runs the priority lines (the special
// distribution of waived SBIC fees), the SBA gates and the fund's waterfall
// on the gross amount and keeps the calculation with the distribution
// (terms_snapshot). Issue is atomic on the server and books the lines AS
// SHOWN: the Distribution fund transaction with per-commitment journals (the
// carry in two legs, owner answer 1) -> status Issued. It is refused while
// the distribution needs Compute, while the lines do not add up to the
// gross, and while a failed SBA gate has no override reason - the form says
// so before the click. Issued distributions are read-only; Revert returns
// them to Draft (refused while a later distribution of the fund is Issued)
// and leaves every amount as issued.
//
// CONSOLIDATED MODE (phase 5): a consolidated fund's distribution is a header
// (fund_group_distributions) with one distributions row per legal entity.
// The LEAD row - the primary fund's - opens in this form and holds the
// inputs; its sources may be realizations of any member fund. Opened with
// ?fund_group= ("New Consolidated Distribution"), the first save goes
// through POST /fund-groups/distributions and the tab morphs onto the lead;
// on an existing row whose detail carries prog__consolidated, Save, Compute,
// Issue, Revert and Delete go through the fund-group endpoints (every entity
// at once), the lines add up to this entity's part of the proceeds, and the
// Override inputs are locked (no overrides this phase). A satellite entity
// row is read-only here with a link to the lead.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import DatePicker from '../../../../core/components/DatePicker.tsx'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { store } from '../../../../state_manager/store.ts'
import type { RootState } from '../../../../state_manager/store.ts'
import type { FundGroupConfigModel } from '../../../../state_manager/data_models/fund_group_config_model.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { removeTab, transformFormTab, updateTabTitle } from '../../../../store/slices/tabSlice'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import NumberText from '../../../../core/components/NumberText.tsx'
import { FundSelect } from '../../../../core/components/FundSelect.tsx'
import { TABLE_DISTRIBUTIONS, TABLE_FUND_TRANSACTIONS } from '../../../../state_manager/globals.ts'
import { useNumberFormat } from '../../../../core/utils/inputNumberFormat.ts'
import type { DistributionKind } from '../../../../state_manager/data_models/waterfall_lookups_model.ts'
import {
  DistributionDetail,
  DistributionPartner,
  DistributionPerCommitment,
  DistributionSavePayload,
  DistributionSourceOption,
  FundGroupDistributionSavePayload
} from '../../../../state_manager/data_models/distribution_detail.ts'
import {
  computeDistribution,
  downloadDistributionWorkbook,
  fetchDistributionDetail,
  fetchDistributionSourceOptions,
  issueDistribution,
  revertDistribution,
  saveDistribution,
  trashDistribution
} from '../../services/distributionsService.ts'
import {
  computeFundGroupDistribution,
  deleteFundGroupDistribution,
  fetchFundGroupDistributionSourceOptions,
  issueFundGroupDistribution,
  revertFundGroupDistribution,
  saveFundGroupDistribution
} from '../../../fund_groups/services/fundGroupsService.ts'
import ConsolidatedDistributionBanner from './ConsolidatedDistributionBanner.tsx'
import DistributionAllocationTable, { PartnerLineKey, partnerLinesEffective } from './DistributionAllocationTable.tsx'
import TakenFirstPanel, { rowEffective } from './TakenFirstPanel.tsx'
import TiersStrip from './TiersStrip.tsx'
import PoolsStrip from './PoolsStrip.tsx'

const { Title, Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
// Status labels the wf__ orchestrators gate on (cfg.distribution_statuses).
const STATUS_LABEL_DRAFT = 'Draft'
const STATUS_LABEL_ISSUED = 'Issued'
// the kinds the engine computes; the rest are reserved (owner answer 7)
const ENGINE_KINDS = new Set(['proceeds', 'return_of_capital'])
// cfg.waterfall_scopes label of a waterfall that runs source by source
const SCOPE_DEAL_BY_DEAL = 'Deal-by-Deal'
// why the Override inputs are locked on a consolidated distribution
const CONSOLIDATED_NO_OVERRIDES = 'No overrides on a consolidated distribution in this phase.'

interface FullDistributionFormProps {
  recordId?: string | null
  isNew?: boolean
}

interface SourceRow {
  key: string
  id?: string
  asset_transaction_id: string | null
  amount: number | null
  description: string
}

let rowKeySeq = 0
const nextRowKey = () => `src-${++rowKeySeq}`

const fmtMoney = (value: number | null | undefined, symbol?: string | null) =>
  value == null ? '-' : `${symbol ?? ''}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`

const round2 = (v: number): number => Math.round(v * 100) / 100

// an investor's cash / carry override, keyed the way the save sends it: the
// commitment and the recipient class of the line's rows (the GP commitment's
// cash is its own-share rows, class GP - the server says which)
const partnerOverrideKey = (partner: DistributionPartner, line: PartnerLineKey): string | null => {
  const cls = partner[line]?.recipient_class
  return cls ? `${partner.fund_commitment_id}:${cls}` : null
}

export default function FullDistributionForm({ recordId: propRecordId, isNew: propIsNew }: FullDistributionFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { openFormInTab, closeFormTab } = useTabManagerContext()
  const [modalApi, modalContextHolder] = Modal.useModal()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId ?? null
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new' || !recordId
  // New-record tabs are keyed per prefill query (tabSlice.addFormTab: the
  // ?fund= / ?asset_transaction= / ?fund_group= openers), so the key must
  // carry the mount-time search string or the title update and the post-save
  // tab swap miss the real tab and a second tab opens beside it. Snapshot it:
  // the global location later reflects whichever tab is active, not this one.
  const [mountSearch] = useState(() => (isNew ? location.search : ''))
  const formTabKey = `${TABLE_DISTRIBUTIONS}-form-${isNew ? 'new' : 'edit'}-${recordId || 'new'}${mountSearch}`

  const kinds = useSelector((state: RootState) => (state.globalConfigs?.distribution_kinds ?? []) as DistributionKind[])
  const fundGroups = useSelector(
    (state: RootState) => (state.globalConfigs?.fund_groups ?? []) as FundGroupConfigModel[]
  )
  const currencyFmt = useNumberFormat('currency')

  const [detail, setDetail] = useState<DistributionDetail | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [notFound, setNotFound] = useState(false)
  const [saving, setSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [dirty, setDirty] = useState(false)

  // Header fields
  const [fundId, setFundId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [kindId, setKindId] = useState<number | null>(null)
  const [distributionDate, setDistributionDate] = useState<string | null>(null)
  const [grossAmount, setGrossAmount] = useState<number | null>(null)
  const [sbaReason, setSbaReason] = useState('')

  // the sources and this session's override edits (the override as typed;
  // null = cleared). Only edited cells are in the maps: every other cell
  // reads its saved override. Two keys, as the save takes them: a priority
  // line by its row id, an investor's cash / carry by commitment and class.
  const [sources, setSources] = useState<SourceRow[]>([])
  const [pcOverrides, setPcOverrides] = useState<Record<string, number | null>>({})
  const [partnerOverrides, setPartnerOverrides] = useState<Record<string, number | null>>({})
  const [downloading, setDownloading] = useState(false)
  const [sourceOptions, setSourceOptions] = useState<DistributionSourceOption[]>([])
  // ?asset_transaction=<id>: a Booked realization's "+ Distribution" opens
  // the form with that realization as the first source
  const [prefillSource, setPrefillSource] = useState<string | null>(null)
  // ?fund_group=<id>: "New Consolidated Distribution" - the first save
  // creates the header and this lead row on the primary fund
  const [newGroupId, setNewGroupId] = useState<string | null>(null)

  const statusLabel = detail?.distribution?.status_label ?? STATUS_LABEL_DRAFT
  const isIssued = statusLabel === STATUS_LABEL_ISSUED
  // consolidated mode: the detail's block on an existing row, the query
  // parameter on a new one
  const consolidated = detail?.distribution?.prog__consolidated ?? null
  const isNewConsolidated = !detail?.distribution?.id && newGroupId != null
  const isConsolidated = consolidated != null || isNewConsolidated
  const consolidatedGroupId = consolidated?.fund_groups__id ?? newGroupId
  // a satellite entity row: derived from the lead, read-only here
  const isSatellite = consolidated != null && !consolidated.is_lead
  // the lifecycle (Compute / Issue / Revert / Delete) runs from this form
  const lifecycleHere = !isSatellite
  const readOnly = isIssued || isSatellite
  const groupName = useMemo(
    () => consolidated?.fund_groups__name ?? fundGroups.find((g) => g.id === consolidatedGroupId)?.name ?? null,
    [consolidated, fundGroups, consolidatedGroupId]
  )
  const currencySymbol = detail?.distribution?.currency_symbol ?? null
  const kindCode = useMemo(() => kinds.find((k) => k.id === kindId)?.code ?? detail?.distribution?.kind_code ?? 'proceeds', [kinds, kindId, detail])
  const isEngineKind = ENGINE_KINDS.has(kindCode)

  const markDirty = () => setDirty(true)

  const hydrateFromDetail = useCallback((d: DistributionDetail) => {
    setDetail(d)
    setFundId(d.distribution.fund_id)
    setName(d.distribution.name ?? '')
    setKindId(d.distribution.kind_id ?? null)
    setDistributionDate(d.distribution.distribution_date ?? null)
    setGrossAmount(d.distribution.gross_amount ?? null)
    setSbaReason(d.distribution.sba_override_reason ?? '')
    setSources(
      d.items.map((i) => ({
        key: nextRowKey(),
        id: i.id,
        asset_transaction_id: i.asset_transaction_id ?? null,
        amount: i.amount,
        description: i.description ?? ''
      }))
    )
    // the Override inputs show the saved overrides again: no unsaved edits
    setPcOverrides({})
    setPartnerOverrides({})
    setDirty(false)
  }, [])

  // Cold-load detail (screen-local; the hard-refresh path is the normal path).
  useEffect(() => {
    let cancelled = false
    if (isNew) {
      const search = new URLSearchParams(mountSearch)
      const fundParam = search.get('fund')
      if (fundParam) setFundId(fundParam)
      const sourceParam = search.get('asset_transaction')
      if (sourceParam) setPrefillSource(sourceParam)
      // ?fund_group=<id> - "New Consolidated Distribution" (the consolidated
      // fund's Distributions tab): the fund is the group's primary fund
      const groupParam = search.get('fund_group')
      if (groupParam) setNewGroupId(groupParam)
      dispatch(updateTabTitle({ key: formTabKey, title: groupParam ? 'New Consolidated Distribution' : 'New Distribution' }))
      return
    }
    ;(async () => {
      setLoading(true)
      const token = getAccessToken(store.getState().auth)
      const res = await fetchDistributionDetail(String(recordId), token)
      if (cancelled) return
      if (res.error || !res.data) {
        if (res.error) showErrorToast(res.error)
        setNotFound(true)
        setLoading(false)
        return
      }
      hydrateFromDetail(res.data)
      dispatch(
        updateTabTitle({
          key: formTabKey,
          title: `${res.data.distribution.name || 'Distribution'} — ${res.data.distribution.fund_name ?? ''}`
        })
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [recordId, isNew, mountSearch, dispatch, formTabKey, hydrateFromDetail])

  // the Booked realizations for the Sources picker: the fund's, or every
  // member fund's on a consolidated distribution
  useEffect(() => {
    let cancelled = false
    if (!fundId && !consolidatedGroupId) {
      setSourceOptions([])
      return
    }
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = consolidatedGroupId
        ? await fetchFundGroupDistributionSourceOptions(consolidatedGroupId, token)
        : await fetchDistributionSourceOptions(fundId as string, token)
      if (cancelled) return
      if (res.error) showErrorToast(res.error)
      setSourceOptions(res.data)
    })()
    return () => {
      cancelled = true
    }
  }, [fundId, consolidatedGroupId])

  // the prefilled source takes its amount from the options once they load
  useEffect(() => {
    if (!prefillSource || sourceOptions.length === 0) return
    const opt = sourceOptions.find((o) => o.value === prefillSource)
    setSources([{ key: nextRowKey(), asset_transaction_id: prefillSource, amount: opt?.amount ?? null, description: '' }])
    if (opt?.amount != null) setGrossAmount(opt.amount)
    setPrefillSource(null)
    setDirty(true)
  }, [prefillSource, sourceOptions])

  function isNewRecord() {
    return !detail?.distribution?.id
  }

  const pcOverride = useCallback(
    (row: DistributionPerCommitment): number | null =>
      row.id in pcOverrides ? pcOverrides[row.id] : row.override_amount ?? null,
    [pcOverrides]
  )
  const setPcOverride = useCallback((rowId: string, value: number | null) => {
    setPcOverrides((prev) => ({ ...prev, [rowId]: value }))
    setDirty(true)
  }, [])

  const partnerOverride = useCallback(
    (partner: DistributionPartner, line: PartnerLineKey): number | null => {
      const key = partnerOverrideKey(partner, line)
      if (key == null) return null
      if (key in partnerOverrides) return partnerOverrides[key]
      const saved = partner[line]
      return saved?.overridden ? saved.override ?? null : null
    },
    [partnerOverrides]
  )
  const setPartnerOverride = useCallback((partner: DistributionPartner, line: PartnerLineKey, value: number | null) => {
    const key = partnerOverrideKey(partner, line)
    if (key == null) return
    setPartnerOverrides((prev) => ({ ...prev, [key]: value }))
    setDirty(true)
  }, [])

  const buildSavePayload = useCallback((): DistributionSavePayload => {
    const payload: DistributionSavePayload = {
      name: name.trim() || null,
      distribution_date: distributionDate,
      kind_id: kindId,
      gross_amount: grossAmount,
      sba_override_reason: sbaReason.trim() || null,
      items: sources
        .filter((s) => s.amount != null || s.asset_transaction_id)
        .map((s, idx) => ({
          ...(s.id ? { id: s.id } : {}),
          asset_transaction_id: s.asset_transaction_id,
          amount: s.amount ?? 0,
          description: s.description.trim() || null,
          display_order: idx + 1
        }))
    }
    if (detail?.distribution?.id) payload.id = detail.distribution.id
    else if (fundId) payload.fund_id = fundId
    // the priority lines by row id; an investor's cash / carry by commitment
    // and class - the server attributes that one to the tier rows
    const overrides = Object.entries(pcOverrides).map(([id, override_amount]) => ({ id, override_amount }))
    if (overrides.length > 0) payload.per_commitment_overrides = overrides
    const partnerEntries = Object.entries(partnerOverrides).map(([key, override_amount]) => {
      const split = key.lastIndexOf(':')
      return {
        fund_commitment_id: key.slice(0, split),
        recipient_class: key.slice(split + 1) as 'LP' | 'GP',
        override_amount
      }
    })
    if (partnerEntries.length > 0) payload.partner_overrides = partnerEntries
    return payload
  }, [name, distributionDate, kindId, grossAmount, sbaReason, sources, detail, fundId, pcOverrides, partnerOverrides])

  // a consolidated distribution saves through the fund-group endpoint: the
  // lead's inputs under the group (create) or the header (edit), no overrides
  const buildConsolidatedSavePayload = useCallback((): FundGroupDistributionSavePayload | null => {
    const { id: _id, fund_id: _fundId, per_commitment_overrides: _pc, partner_overrides: _po, ...inputs } = buildSavePayload()
    if (consolidated) return { ...inputs, fund_group_distribution_id: consolidated.fund_group_distributions__id }
    if (newGroupId) return { ...inputs, fund_group_id: newGroupId }
    return null
  }, [buildSavePayload, consolidated, newGroupId])

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (readOnly) return false
    if (!fundId) {
      showErrorToast('Select a fund')
      return false
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const consolidatedPayload = isConsolidated ? buildConsolidatedSavePayload() : null
      const res = consolidatedPayload
        ? await saveFundGroupDistribution(consolidatedPayload, token)
        : await saveDistribution(buildSavePayload(), token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to save the distribution')
        return false
      }
      const wasNew = !detail?.distribution?.id
      hydrateFromDetail(res.data)
      toast.success(
        wasNew
          ? consolidatedPayload
            ? 'Consolidated distribution created'
            : 'Distribution created'
          : 'Distribution saved'
      )
      if (wasNew) {
        // morph the "new" tab into the edit tab in place
        dispatch(
          transformFormTab({
            key: formTabKey,
            module: TABLE_DISTRIBUTIONS,
            recordId: res.data.distribution.id,
            title: `${res.data.distribution.name || 'Distribution'} — ${res.data.distribution.fund_name ?? ''}`
          })
        )
        navigate(`/${TABLE_DISTRIBUTIONS}/forms/full/${res.data.distribution.id}`, { replace: true })
      }
      return true
    } finally {
      setSaving(false)
    }
  }, [readOnly, fundId, isConsolidated, buildConsolidatedSavePayload, buildSavePayload, detail, hydrateFromDetail, dispatch, formTabKey, navigate])

  // Compute / Recompute: recalculating is its own step. Unsaved changes are
  // saved first, so a recompute never discards what was typed; the engine
  // matches the lines by key, so an override on a line that is still there
  // survives.
  const handleCompute = useCallback(async () => {
    if (!detail?.distribution?.id) return
    if (dirty) {
      const saved = await handleSave()
      if (!saved) return
    }
    setActionLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = consolidated
        ? await computeFundGroupDistribution(consolidated.fund_group_distributions__id, token)
        : await computeDistribution(detail.distribution.id, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to compute the distribution')
        return
      }
      hydrateFromDetail(res.data)
      const failed = (res.data.compute as { sba_failed?: number } | null | undefined)?.sba_failed ?? 0
      toast.success(
        failed > 0
          ? `Lines computed — ${failed} SBA gate(s) failed; record the override reason before issuing`
          : 'Lines computed'
      )
    } finally {
      setActionLoading(false)
    }
  }, [detail, consolidated, dirty, handleSave, hydrateFromDetail])

  const reloadDetail = useCallback(async () => {
    if (!detail?.distribution?.id) return
    const token = getAccessToken(store.getState().auth)
    const res = await fetchDistributionDetail(detail.distribution.id, token)
    if (res.error || !res.data) {
      showErrorToast(res.error || 'Failed to reload the distribution')
      return
    }
    hydrateFromDetail(res.data)
  }, [detail, hydrateFromDetail])

  const handleIssue = useCallback(() => {
    if (!detail?.distribution?.id) return
    const partners = detail.partners.length
    modalApi.confirm({
      title: consolidated ? 'Issue this consolidated distribution?' : 'Issue this distribution?',
      content: consolidated ? (
        <div>
          <p>
            Every legal entity books its share: each entity&apos;s lines are booked as they stand - a booked
            Distribution transaction per entity with per-commitment journals (the carry reallocated to the GP
            commitment, then paid) - and the consolidated distribution becomes read-only.
          </p>
          <ul className='mb-0 pl-4'>
            {consolidated.entities.map((e) => (
              <li key={e.distributions__id}>
                <Text strong>{e.funds__name ?? 'Entity'}</Text>
                {' — '}
                {fmtMoney(e.prog__effective_total ?? e.entity_gross, currencySymbol)}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div>
          <p>
            The lines shown are booked as they stand: a booked Distribution transaction is created with
            per-commitment journals (the carry reallocated to the GP commitment, then paid), and the
            distribution becomes read-only.
          </p>
          <p className='mb-0'>
            <Text strong>{detail.distribution.fund_name}</Text>
            {' — '}
            {fmtMoney(detail.totals.effective_total, currencySymbol)}
            {partners ? ` across ${partners} partners` : ''}
          </p>
        </div>
      ),
      okText: 'Issue',
      onOk: async () => {
        const token = getAccessToken(store.getState().auth)
        const res = consolidated
          ? await issueFundGroupDistribution(consolidated.fund_group_distributions__id, token)
          : await issueDistribution(detail.distribution.id, token)
        if (res.error || !res.data) {
          showErrorToast(res.error || 'Failed to issue the distribution')
          void reloadDetail()
          return
        }
        hydrateFromDetail(res.data)
        toast.success(consolidated ? 'Consolidated distribution issued: every entity booked' : 'Distribution issued and booked')
      }
    })
  }, [detail, consolidated, currencySymbol, hydrateFromDetail, modalApi, reloadDetail])

  const handleRevert = useCallback(() => {
    if (!detail?.distribution?.id) return
    modalApi.confirm({
      title: consolidated ? 'Revert this consolidated distribution to Draft?' : 'Revert this distribution to Draft?',
      content: consolidated
        ? 'Every entity returns to Draft: its booked Distribution transaction and journals are removed, the lines keep their amounts. Refused while a later distribution of a member fund is Issued, or while the journals sit in a closed financial year.'
        : 'The booked Distribution transaction and its journals are removed; the lines keep their amounts. Refused while a later distribution of the fund is Issued, or while the journals sit in a closed financial year.',
      okText: 'Revert',
      onOk: async () => {
        const token = getAccessToken(store.getState().auth)
        const res = consolidated
          ? await revertFundGroupDistribution(consolidated.fund_group_distributions__id, token)
          : await revertDistribution(detail.distribution.id, token)
        if (res.error || !res.data) {
          showErrorToast(res.error || 'Failed to revert the distribution')
          return
        }
        hydrateFromDetail(res.data)
        toast.success(consolidated ? 'Consolidated distribution reverted to Draft' : 'Distribution reverted to Draft')
      }
    })
  }, [detail, consolidated, hydrateFromDetail, modalApi])

  const handleDelete = useCallback(() => {
    if (!detail?.distribution?.id) return
    modalApi.confirm({
      title: consolidated ? 'Delete this consolidated distribution?' : 'Delete this distribution?',
      content: consolidated
        ? 'The consolidated distribution and the row of every legal entity, with their sources and lines, will be moved to trash.'
        : 'The distribution, its sources and its lines will be moved to trash.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const token = getAccessToken(store.getState().auth)
        const res = consolidated
          ? await deleteFundGroupDistribution(consolidated.fund_group_distributions__id, token)
          : await trashDistribution(detail.distribution.id, token)
        if (!res.success) {
          showErrorToast(res.error || 'Failed to delete the distribution')
          return
        }
        toast.success(consolidated ? 'Consolidated distribution deleted' : 'Distribution deleted')
        // leave the form before the tab goes (the capital call form's order):
        // the hub is the nearest list of what was booked
        navigate(`/${TABLE_FUND_TRANSACTIONS}`)
        dispatch(removeTab({ key: formTabKey }))
      }
    })
  }, [detail, consolidated, dispatch, navigate, formTabKey, modalApi])

  // the audit workbook is built from the saved rows
  const handleDownloadWorkbook = useCallback(async () => {
    if (!detail?.distribution?.id) return
    setDownloading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await downloadDistributionWorkbook(detail.distribution.id, token)
      if (!res.success) showErrorToast(res.error || 'Failed to download the workbook')
    } finally {
      setDownloading(false)
    }
  }, [detail])

  // ----- what stands between a Draft and Issue -----
  const rows = useMemo(() => detail?.per_commitment ?? [], [detail])
  const partners = useMemo(() => detail?.partners ?? [], [detail])
  const hasLines = rows.length > 0
  const needsCompute = Boolean(detail?.distribution?.id) && !isIssued && detail?.distribution?.needs_compute === true
  // what is taken first per commitment as it stands: the priority rows, this session's edits included
  const takenFirst = useMemo(() => {
    const byCommitment: Record<string, number> = {}
    for (const r of rows) {
      if (r.kind !== 'priority') continue
      byCommitment[r.fund_commitment_id] = (byCommitment[r.fund_commitment_id] ?? 0) + (rowEffective(r, pcOverride) ?? 0)
    }
    return byCommitment
  }, [rows, pcOverride])
  // the lines as they stand: every investor's cash and carry, and what is taken first
  const effectiveTotal = useMemo(
    () =>
      round2(
        partners.reduce((acc, p) => acc + partnerLinesEffective(p, partnerOverride), 0) +
          Object.values(takenFirst).reduce((acc, v) => acc + v, 0)
      ),
    [partners, partnerOverride, takenFirst]
  )
  const workbookBlockedReason: string | null = !hasLines ? 'Compute first.' : dirty ? 'Save your changes first.' : null
  // what the lines must add up to: the gross amount - or, on a consolidated
  // distribution, this entity's part of the proceeds (the lead's lines are
  // the lead entity's, not the whole)
  const entityGross = consolidated?.entity_gross ?? null
  const linesTarget = entityGross ?? grossAmount
  const residual = round2((linesTarget ?? 0) - effectiveTotal)
  const sbaFailed = detail?.distribution?.prog__sba_failed === true
  const issueBlockedReason: string | null = dirty
    ? 'Save your changes first.'
    : needsCompute
      ? hasLines
        ? 'Recompute first: the inputs changed since the lines were computed.'
        : 'Compute the lines first.'
      : !hasLines
        ? 'Compute the lines first.'
        : residual !== 0
          ? entityGross != null
            ? `The lines add up to ${fmtMoney(effectiveTotal, currencySymbol)}, not this entity's part of the proceeds of ${fmtMoney(entityGross, currencySymbol)}.`
            : `The lines add up to ${fmtMoney(effectiveTotal, currencySymbol)}, not the gross amount of ${fmtMoney(grossAmount, currencySymbol)}.`
          : sbaFailed && !(detail?.distribution?.sba_override_reason ?? '').trim()
            ? 'Record the SBA override reason on the Taken first panel, then Save.'
            : null

  const kindOptions = useMemo(
    () => kinds.map((k) => ({ value: k.id, label: k.label, title: k.description ?? undefined })),
    [kinds]
  )
  const selectedKind = kinds.find((k) => k.id === kindId) ?? null
  const reservedKind = selectedKind != null && !ENGINE_KINDS.has(selectedKind.code) && selectedKind.code !== 'special_distribution'

  // ----- sources -----
  const updateSource = (key: string, patch: Partial<SourceRow>) => {
    setSources((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)))
    markDirty()
  }
  // a fund whose waterfall has pools: its realizations name the pool they belong to
  const pooledFund = sourceOptions.some((o) => o.pool_code != null)
  // The picker's options: the fund's realizations, plus any source this
  // distribution already names that is not among them - a disposal for no
  // consideration realizes nothing to offer, yet a distribution may name it at
  // 0. Such a source is labelled from the item itself, the way the server
  // labels an option, so it never shows as a bare id.
  const realizationOptions = useMemo(() => {
    const offered = sourceOptions.map((o) => ({ value: o.value, label: o.label }))
    const known = new Set(sourceOptions.map((o) => o.value))
    const named = (detail?.items ?? []).flatMap((i) => {
      const id = i.asset_transaction_id
      if (!id || known.has(id)) return []
      known.add(id)
      const dated = i.asset_transactions__transaction_date
      return [
        {
          value: id,
          label: [
            `${i.accounts__name ?? 'Unnamed company'} - ${i.asset_transaction_type_label ?? 'Realization'}`,
            // the date as the server states it, not shifted to the browser's time zone
            dated ? dayjs(dated.slice(0, 10)).format(DATE_FORMAT) : null
          ]
            .filter(Boolean)
            .join(' ')
        }
      ]
    })
    return [...offered, ...named]
  }, [sourceOptions, detail])
  const sourceColumns: ColumnsType<SourceRow> = [
    {
      title: 'Realization',
      dataIndex: 'asset_transaction_id',
      render: (v: string | null, row) => (
        <Select
          className='w-full'
          allowClear
          showSearch
          optionFilterProp='label'
          placeholder={
            pooledFund
              ? 'A Booked realization of the fund'
              : 'A Booked realization of the fund (optional for a Whole Fund distribution)'
          }
          value={v ?? undefined}
          disabled={readOnly}
          options={realizationOptions}
          onChange={(value) => {
            const opt = sourceOptions.find((o) => o.value === value)
            updateSource(row.key, {
              asset_transaction_id: value ?? null,
              amount: row.amount ?? opt?.amount ?? null
            })
          }}
        />
      )
    },
    ...(pooledFund
      ? [
          {
            // the pool is the source's: the year its investment was bought
            title: 'Pool',
            key: 'pool',
            width: 110,
            render: (_: unknown, row: SourceRow) => {
              if (!row.asset_transaction_id) return null
              const code =
                sourceOptions.find((o) => o.value === row.asset_transaction_id)?.pool_code ??
                detail?.items.find((i) => i.asset_transaction_id === row.asset_transaction_id)?.prog__pool_code
              return code ? <Tag color='blue'>{code}</Tag> : <Tag color='gold'>No pool</Tag>
            }
          }
        ]
      : []),
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 180,
      render: (v: number | null, row) => (
        <InputNumber
          className='w-full'
          aria-label='Source amount'
          value={v}
          disabled={readOnly}
          formatter={currencyFmt.formatter}
          parser={currencyFmt.parser}
          onChange={(value) => updateSource(row.key, { amount: value == null ? null : Number(value) })}
        />
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 240,
      render: (v: string, row) => (
        <Input value={v} disabled={readOnly} onChange={(e) => updateSource(row.key, { description: e.target.value })} />
      )
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_: unknown, row) =>
        readOnly ? null : (
          <Button
            type='text'
            size='small'
            danger
            aria-label='Remove source'
            icon={<DeleteOutlined />}
            onClick={() => {
              setSources((prev) => prev.filter((s) => s.key !== row.key))
              markDirty()
            }}
          />
        )
    }
  ]
  const sourcesTotal = round2(sources.reduce((acc, s) => acc + (s.amount ?? 0), 0))

  const totalsRows = useMemo(() => {
    const t = detail?.totals
    if (!t) return []
    return [
      { key: 'gross', label: 'Gross amount', value: grossAmount },
      ...(entityGross != null ? [{ key: 'entity', label: "This entity's part of the proceeds", value: entityGross }] : []),
      { key: 'priority', label: 'Taken first (priority lines)', value: t.priority },
      { key: 'lp', label: 'LP tier lines', value: t.lp },
      { key: 'carry', label: 'Carry (GP tier lines)', value: t.carry },
      { key: 'own', label: "The GP commitment's own share", value: t.gp_own_share },
      { key: 'roc', label: 'of which return of capital', value: t.return_of_capital },
      { key: 'effective', label: 'Lines total (as they stand)', value: effectiveTotal },
      { key: 'residual', label: entityGross != null ? "Residual against the entity's part" : 'Residual against the gross', value: residual }
    ]
  }, [detail, grossAmount, entityGross, effectiveTotal, residual])

  const linkedTransactionCard =
    detail?.transaction?.id != null ? (
      <FormSection title='Linked Fund Transaction' titleColor='gray' scrollable={false}>
        <Space size='large' wrap>
          <span>
            Status:{' '}
            <Tag color={detail.transaction.status_label === 'Booked' ? 'green' : 'blue'}>
              {detail.transaction.status_label}
            </Tag>
          </span>
          <span>Journals: {detail.transaction.journal_count ?? 0}</span>
          <span>Total debits: {fmtMoney(detail.transaction.total_debits, currencySymbol)}</span>
          <Button
            size='small'
            onClick={() =>
              openFormInTab(TABLE_FUND_TRANSACTIONS, detail.transaction!.id, `Distribution — ${detail.distribution.fund_name ?? ''}`)
            }
          >
            Open Transaction
          </Button>
        </Space>
      </FormSection>
    ) : null

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    )
  }
  if (notFound) {
    return (
      <div className='p-6'>
        <Alert type='error' showIcon message='Distribution not found' />
      </div>
    )
  }

  return (
    <div className='h-full w-full min-h-0 flex flex-col bg-gray-50 p-3 overflow-hidden'>
      {modalContextHolder}
      <Card size='small' className='flex-1 min-h-0 flex flex-col h-full overflow-hidden' classNames={{ body: 'h-full p-3 overflow-y-auto' }}>
        <div className='flex items-center justify-between mb-3 flex-wrap gap-2'>
          <Space align='center'>
            <Title level={5} className='!mb-0'>
              {isNewRecord()
                ? isNewConsolidated
                  ? 'New Consolidated Distribution'
                  : 'New Distribution'
                : `${detail?.distribution.name || 'Distribution'} — ${detail?.distribution.fund_name ?? ''}`}
            </Title>
            <Tag color={isIssued ? 'green' : 'blue'}>{statusLabel}</Tag>
            {detail?.distribution?.kind_label ? <Tag>{detail.distribution.kind_label}</Tag> : null}
          </Space>
          <Space>
            {/* the fixed button grammar: destructive leftmost, primary rightmost */}
            {!isNewRecord() && !isIssued && lifecycleHere ? (
              <Button danger onClick={handleDelete}>
                Delete
              </Button>
            ) : null}
            {isIssued && lifecycleHere ? (
              <Button loading={actionLoading} onClick={handleRevert}>
                Revert to Draft
              </Button>
            ) : null}
            {!isNewRecord() ? (
              <Tooltip title={workbookBlockedReason}>
                <span>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={downloading}
                    disabled={workbookBlockedReason != null}
                    onClick={handleDownloadWorkbook}
                  >
                    Download workbook
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {!isNewRecord() && !isIssued && lifecycleHere ? (
              <Button loading={actionLoading || saving} onClick={handleCompute}>
                {hasLines ? 'Recompute' : 'Compute'}
              </Button>
            ) : null}
            {!isNewRecord() && !isIssued && lifecycleHere ? (
              <Tooltip title={issueBlockedReason}>
                <span>
                  <Button type='primary' ghost loading={actionLoading} disabled={issueBlockedReason != null} onClick={handleIssue}>
                    Issue
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {!readOnly ? (
              <Button type='primary' loading={saving} onClick={handleSave}>
                {isNewRecord() ? 'Create' : 'Save'}
              </Button>
            ) : (
              <Button type='primary' onClick={() => closeFormTab(formTabKey)}>
                Close
              </Button>
            )}
          </Space>
        </div>

        <div className='flex flex-col gap-3'>
          {isConsolidated ? (
            <ConsolidatedDistributionBanner
              mode={isNewConsolidated ? 'new' : isSatellite ? 'entity' : 'lead'}
              groupName={groupName}
              consolidated={consolidated}
              onOpenLead={() => {
                if (consolidated?.lead_distribution_id)
                  openFormInTab(TABLE_DISTRIBUTIONS, consolidated.lead_distribution_id, `${name || 'Distribution'} — ${groupName ?? ''}`)
              }}
            />
          ) : null}
          {needsCompute && hasLines ? (
            <Alert
              type='warning'
              showIcon
              message='Recompute before issuing'
              description='The inputs of this distribution changed since its lines were computed, so the lines no longer match them. Press Recompute, check the lines, then Issue.'
            />
          ) : null}
          {!isIssued && hasLines && residual !== 0 && !dirty ? (
            <Alert
              type='warning'
              showIcon
              message={entityGross != null ? "The lines do not add up to this entity's part of the proceeds" : 'The lines do not add up to the gross amount'}
              description={
                entityGross != null
                  ? `They add up to ${fmtMoney(effectiveTotal, currencySymbol)} against this entity's part of ${fmtMoney(entityGross, currencySymbol)}. Change the gross amount or the sources and Recompute.`
                  : `They add up to ${fmtMoney(effectiveTotal, currencySymbol)} against a gross of ${fmtMoney(grossAmount, currencySymbol)}. Rebalance the overrides, or change the gross amount and Recompute.`
              }
            />
          ) : null}
          {!isIssued && sbaFailed ? (
            <Alert
              type='warning'
              showIcon
              message='SBA gates failed on Compute'
              description='The Taken first panel lists them. Record the override reason there and Save; Issue proceeds with the reason stored on the distribution (owner, 2026-10-06).'
            />
          ) : null}
          {detail?.distribution?.id && isEngineKind && detail.distribution.prog__has_waterfall === false ? (
            <Alert
              type='info'
              showIcon
              message='The fund has no waterfall'
              description='Set one up on the fund Terms tab (Waterfall section) before computing: the engine needs the tiers.'
            />
          ) : null}
          {reservedKind ? (
            <Alert
              type='info'
              showIcon
              message={`${selectedKind?.label} is reserved`}
              description='The kind exists, but the engine does not compute it until a fund needs it (owner, 2026-10-06, answer 7).'
            />
          ) : null}

          <FormSection title='Details' titleColor='blue' scrollable={false}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <div className='mb-1 text-gray-500 text-sm'>Fund</div>
                <FundSelect
                  className='w-full'
                  value={fundId}
                  placeholder='Select fund...'
                  disabled={readOnly || !isNewRecord() || isNewConsolidated}
                  onChange={(v) => {
                    setFundId(v)
                    markDirty()
                  }}
                />
              </Col>
              <Col xs={24} md={8}>
                <div className='mb-1 text-gray-500 text-sm'>Name</div>
                <Input
                  value={name}
                  placeholder='e.g. Distribution 1 - Acme exit'
                  disabled={readOnly}
                  onChange={(e) => {
                    setName(e.target.value)
                    markDirty()
                  }}
                />
              </Col>
              <Col xs={24} md={8}>
                <div className='mb-1 text-gray-500 text-sm'>Kind</div>
                <Select
                  className='w-full'
                  aria-label='Kind'
                  placeholder='Proceeds'
                  value={kindId ?? undefined}
                  disabled={readOnly}
                  options={kindOptions}
                  onChange={(v) => {
                    setKindId(v)
                    markDirty()
                  }}
                />
              </Col>
              <Col xs={12} md={8}>
                <div className='mb-1 text-gray-500 text-sm'>Distribution Date</div>
                <DatePicker
                  className='w-full'
                  value={distributionDate ? dayjs(distributionDate) : null}
                  format={DATE_FORMAT}
                  disabled={readOnly}
                  onChange={(d) => {
                    setDistributionDate(d ? d.format('YYYY-MM-DD') : null)
                    markDirty()
                  }}
                />
              </Col>
              <Col xs={12} md={8}>
                <div className='mb-1 text-gray-500 text-sm'>Gross Amount</div>
                <InputNumber
                  className='w-full'
                  aria-label='Gross amount'
                  value={grossAmount}
                  disabled={readOnly}
                  formatter={currencyFmt.formatter}
                  parser={currencyFmt.parser}
                  onChange={(v) => {
                    setGrossAmount(v == null ? null : Number(v))
                    markDirty()
                  }}
                />
              </Col>
              {detail?.distribution?.prog__scope_label ? (
                <Col xs={24} md={8}>
                  <div className='mb-1 text-gray-500 text-sm'>Waterfall scope</div>
                  <div className='pt-1'>{detail.distribution.prog__scope_label}</div>
                </Col>
              ) : null}
            </Row>
          </FormSection>

          <FormSection
            title='Sources'
            titleColor='blue'
            scrollable={false}
            headerActions={
              !readOnly ? (
                <Button
                  size='small'
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSources((prev) => [...prev, { key: nextRowKey(), asset_transaction_id: null, amount: null, description: '' }])
                    markDirty()
                  }}
                >
                  Add source
                </Button>
              ) : null
            }
          >
            <Text type='secondary' className='block mb-2'>
              The realizations the proceeds come from. A Whole Fund distribution needs none (the gross amount is
              enough); a Deal-by-Deal distribution names the realization each source comes from. The sources
              must add up to the gross amount, or leave the gross empty to take their sum.
              {isConsolidated
                ? ' A consolidated distribution may name the realizations of any member fund; each entity takes its part of the proceeds.'
                : ''}
              {pooledFund
                ? ' This fund\u2019s waterfall has pools: every source names its realization, and the realization\u2019s pool decides whose capital the money returns.'
                : ''}
            </Text>
            {sources.length === 0 ? (
              <Text type='secondary'>No sources.</Text>
            ) : (
              <Table<SourceRow>
                size='small'
                rowKey='key'
                pagination={false}
                columns={sourceColumns}
                dataSource={sources}
                summary={() =>
                  sources.length > 1 ? (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={pooledFund ? 2 : 1}>
                          <Text strong>Total</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <NumberText value={sourcesTotal} wrap={(f) => <Text strong>{f}</Text>} />
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} colSpan={2} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  ) : null
                }
              />
            )}
          </FormSection>

          <FormSection title='Taken first' titleColor='orange' scrollable={false}>
            <TakenFirstPanel
              detail={detail}
              reason={sbaReason}
              onReasonChange={(v) => {
                setSbaReason(v)
                markDirty()
              }}
              readOnly={readOnly}
              overridesLockedReason={isConsolidated ? CONSOLIDATED_NO_OVERRIDES : null}
              pcOverride={pcOverride}
              setPcOverride={setPcOverride}
              currencyFmt={currencyFmt}
            />
          </FormSection>

          <FormSection title='Allocation' titleColor='indigo' scrollable={false}>
            {isNewRecord() ? (
              <Text type='secondary'>Save the distribution, then Compute to allocate the lines per partner and tier.</Text>
            ) : !hasLines ? (
              <Text type='secondary'>No lines yet — run Compute to populate this table.</Text>
            ) : (
              <>
                {dirty ? (
                  <Alert
                    className='mb-2'
                    type='info'
                    showIcon
                    message='Unsaved changes: the tier cells show the saved attribution. Save to see an override spread over the tiers. Recompute saves first; an override survives it.'
                  />
                ) : null}
                {isConsolidated ? (
                  <Text type='secondary' className='block mb-2'>
                    {CONSOLIDATED_NO_OVERRIDES} The lines are this entity&apos;s part of the proceeds, as its
                    waterfall allocates them.
                  </Text>
                ) : null}
                <TiersStrip
                  tiers={detail?.tiers ?? []}
                  basis={detail?.distribution?.prog__basis}
                  basisLabel={detail?.distribution?.prog__basis_label}
                  historyCheckLabel={detail?.distribution?.prog__history_check_label}
                  readOnly={readOnly}
                />
                <PoolsStrip pools={detail?.pools ?? []} tiers={detail?.tiers ?? []} />
                <DistributionAllocationTable
                  partners={partners}
                  tiers={detail?.tiers ?? []}
                  historyCheck={detail?.distribution?.prog__history_check}
                  dealByDeal={(detail?.distribution?.prog__scope ?? detail?.distribution?.prog__scope_label) === SCOPE_DEAL_BY_DEAL}
                  readOnly={readOnly}
                  overridesLockedReason={isConsolidated ? CONSOLIDATED_NO_OVERRIDES : null}
                  currencyFmt={currencyFmt}
                  override={partnerOverride}
                  setOverride={setPartnerOverride}
                  takenFirst={takenFirst}
                />
              </>
            )}
          </FormSection>

          <FormSection title='Totals' titleColor='green' scrollable={false}>
            {totalsRows.length === 0 ? (
              <Text type='secondary'>Compute to see the totals.</Text>
            ) : (
              <Table
                size='small'
                pagination={false}
                rowKey='key'
                columns={[
                  { title: '', dataIndex: 'label' },
                  {
                    title: 'Amount',
                    dataIndex: 'value',
                    width: 180,
                    align: 'right' as const,
                    render: (v: number | null, row: { key: string }) =>
                      row.key === 'residual' && v !== 0 ? (
                        <NumberText value={v} wrap={(f) => <Text type='danger'>{f}</Text>} />
                      ) : (
                        <NumberText value={v} />
                      )
                  }
                ]}
                dataSource={totalsRows}
              />
            )}
          </FormSection>

          {linkedTransactionCard}
        </div>
      </Card>
    </div>
  )
}
